import { create } from 'zustand'

export interface Toast {
  id: string
  type: 'success' | 'error' | 'info'
  message: string
  // Set just before actual removal so ToastContainer can play a slide-up-and-fade exit
  // animation instead of the toast disappearing instantly — see removeToast/dismissToast.
  exiting?: boolean
}

// How long the slide-up-and-fade exit animation takes — dismissToast holds the toast in state
// with exiting: true for this long before removeToast actually drops it, so the CSS animation
// (see .toast-exit in index.css) has time to finish playing.
export const TOAST_EXIT_DURATION_MS = 200

interface UIState {
  sidebarCollapsed: boolean
  brandSwitcherOpen: boolean
  toasts: Toast[]
  // Internal admins bypass the trial/subscription lockout by default — flipping this lets them
  // preview the app as a regular (locked-out) admin would see it. Resets on reload.
  previewAsRegularAdmin: boolean
  // Set right before navigating away to a page that should celebrate an action taken elsewhere
  // (e.g. Submit for Edit from Clip Archive navigating to Project Tracker/Rolla Editing Orders) —
  // the destination page reads + clears it on mount via consumePendingConfetti() so the
  // celebration survives the route change without threading it through router state.
  pendingConfetti: boolean

  toggleSidebar: () => void
  openBrandSwitcher: () => void
  closeBrandSwitcher: () => void
  togglePreviewAsRegularAdmin: () => void
  triggerPendingConfetti: () => void
  consumePendingConfetti: () => boolean
  addToast: (toast: Omit<Toast, 'id'>) => void
  // Marks a toast as exiting (triggers the CSS exit animation), then removes it from state once
  // that animation has had time to finish. This is what auto-dismiss and the manual X button
  // both call — removeToast itself stays available for an instant, no-animation removal.
  dismissToast: (id: string) => void
  removeToast: (id: string) => void
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarCollapsed: false,
  brandSwitcherOpen: false,
  toasts: [],
  previewAsRegularAdmin: false,
  pendingConfetti: false,

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  openBrandSwitcher: () => set({ brandSwitcherOpen: true }),
  closeBrandSwitcher: () => set({ brandSwitcherOpen: false }),
  togglePreviewAsRegularAdmin: () => set((s) => ({ previewAsRegularAdmin: !s.previewAsRegularAdmin })),
  triggerPendingConfetti: () => set({ pendingConfetti: true }),
  consumePendingConfetti: () => {
    const wasPending = get().pendingConfetti
    if (wasPending) set({ pendingConfetti: false })
    return wasPending
  },

  addToast: (toast) =>
    set((s) => {
      const id = crypto.randomUUID()
      setTimeout(() => {
        useUIStore.getState().dismissToast(id)
      }, 4000)
      return { toasts: [...s.toasts, { ...toast, id }] }
    }),

  dismissToast: (id) => {
    set((s) => ({ toasts: s.toasts.map((t) => (t.id === id ? { ...t, exiting: true } : t)) }))
    setTimeout(() => {
      useUIStore.getState().removeToast(id)
    }, TOAST_EXIT_DURATION_MS)
  },

  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))
